import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';

const TimeLogs = ({ user, token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;

  //  UTC convert to IST 
  const convertToIST = (utcDate) => {
    if (!utcDate) return null;
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) return utcDate;
    
    // Convert to IST and format as 12-hour
    const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    
    const rawHours = local.getHours();
    const isPM = rawHours >= 12;
    let hours12 = rawHours % 12;
    if (hours12 === 0) hours12 = 12;
    
    const hours = String(hours12).padStart(2, '0');
    const mins = String(local.getMinutes()).padStart(2, '0');
    const secs = String(local.getSeconds()).padStart(2, '0');
    const ampm = isPM ? 'PM' : 'AM';
    
    return `${day}-${month}-${year}, ${hours}:${mins}:${secs} ${ampm}`;
  };

  //  Date 
  const getDateOnly = (utcDate) => {
    if (!utcDate) return 'N/A';
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) return 'N/A';
    
    const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  const fetchTimeLogs = async () => {
    if (!token || !user?.email) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendances/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeLogs();
  }, [user, token]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'Active';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="timelogs-container">
      <div className="timelogs-header">
        <h3>Time Logs</h3>
        <button onClick={fetchTimeLogs} disabled={loading} className="refresh-btn">
          {loading ? '' : <RefreshCw size={16} />}
        </button>
      </div>

      <div className="timelogs-list">
        {logs.length === 0 ? (
          <div className="no-logs">{loading ? 'Loading...' : 'No time logs available'}</div>
        ) : (
          logs.map((log) => {
            const checkInIST = convertToIST(log.check_in);
            const checkOutIST = convertToIST(log.check_out);
            const dateOnly = getDateOnly(log.check_in);
            
            return (
              <div key={log.id} className="timelog-item">
                <div className="timelog-date">{dateOnly}</div>
                <div className="timelog-times">
                  <span className="checkin">
                    <CheckCircle size={16} color="green" />
                    {checkInIST || 'N/A'}
                  </span>
                  {checkOutIST && (
                    <span className="checkout">
                      <XCircle size={16} color="#f59e0b" />
                      {checkOutIST}
                    </span>
                  )}
                </div>
                <div className="timelog-duration">
                  <Clock size={16} />
                  {formatDuration(log.duration_seconds)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TimeLogs;